import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

function formatCount(num: number): string {
  if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
  return String(num);
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-2">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.imageUrl,
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '吃瓜爆料',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://chigua-baoliao.com/article/${article.id}`,
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chigua-baoliao.com' },
          ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://chigua-baoliao.com/category/${category.slug}` }] : []),
          { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
        ],
      }) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1024px] mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]}
              />
            </div>

            <article className="pb-12">
              <header className="mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-snug mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatCount(article.readCount)} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {formatCount(article.commentCount)} 评论
                  </span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
              </header>

              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={article.title}
                />
              </div>

              <div
                className="prose-content text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
                <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-background-200 text-sm text-foreground-600 cursor-pointer transition-colors">
                  <i className="ri-thumb-up-line"></i> 点赞
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-background-200 text-sm text-foreground-600 cursor-pointer transition-colors">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-background-200 text-sm text-foreground-600 cursor-pointer transition-colors">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="pb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-800 mb-5">
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>

      <style>{`
        .prose-content h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-900));
        }
        .prose-content h3 {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: oklch(var(--foreground-800));
        }
        .prose-content p {
          margin-bottom: 1rem;
          line-height: 1.85;
          color: oklch(var(--foreground-800));
        }
      `}</style>
    </>
  );
}